import ColumnFilter from "components/ColumnFilter";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import { isEmpty, noop, uniqBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProductAPI from "restAPI/ProductAPI";
import { BaseResponseDto } from "types/api/baseResponseDto";
import { NameIdPair } from "types/api/nameIdPair";
import { FilterInfo } from "types/pagination";

import useUrlFilters from "./useUrlFilters";

const useProductFilterHelper = ({ filters }: { filters: FilterInfo }) => {
  const [loading, setLoading] = useState(true);
  const [allOptions, setAllOptions] = useState<NameIdPair[]>([]);
  const [options, setOptions] = useState<NameIdPair[]>([]);
  const [searchText, setSearchText] = useState("");
  const appliedArmorcodeGroups = useUrlFilters("groups");
  const globalFilter = useGlobalFilterContext();

  useEffect(() => {
    let cancelFn = noop;
    const timeout = setTimeout(() => {
      setLoading(true);
      if (searchText) {
        setOptions([]);
      }
      // filters from url/dashboard should take priority
      const { cancel, promise } = ProductAPI.getAllQuick_V2({
        groups: !isEmpty(appliedArmorcodeGroups)
          ? appliedArmorcodeGroups
          : filters?.groups,
        search: searchText,
      });
      promise.then((response: BaseResponseDto<NameIdPair[]>) => {
        setOptions(response.data);
        setAllOptions((prev) => uniqBy([...prev, ...response.data], "id"));
        setLoading(false);
      });
      cancelFn = cancel;
    }, 500);
    return () => {
      clearTimeout(timeout);
      cancelFn();
    };
  }, [
    globalFilter.globalFilterHash,
    appliedArmorcodeGroups,
    filters?.groups,
    searchText,
  ]);

  const renderFilterDropdown = useCallback(
    (props) => {
      return (
        <ColumnFilter
          {...props}
          filters={options}
          onSearchChange={setSearchText}
          loading={loading}
          allowEmptySearch
          localSearch={false}
        />
      );
    },
    [options, loading],
  );

  return useMemo(
    () => ({
      loading,
      options,
      allOptions,
      onSearchChange: setSearchText,
      renderFilterDropdown,
    }),
    [loading, options, allOptions, renderFilterDropdown],
  );
};

export default useProductFilterHelper;
